// BM25 from scratch — classic Robertson-Sparck Jones model.
// Standard params: k1=1.5, b=0.75. No external dependencies.
//
// Reference: Robertson, S. & Zaragoza, H. (2009)
//   "The Probabilistic Relevance Framework: BM25 and Beyond"
//
// Why BM25 (not just embeddings)?
//   - Term-exact matches beat embeddings on names ("MCP-JEST", "Plum", "Coupang").
//   - Subsecond, no model load required.
//   - Hybrid (BM25 ∪ embeddings) consistently beats either alone in IR research.

type Doc = { id: string; text: string };

const STOPWORDS = new Set([
  "a", "an", "the", "and", "or", "but", "of", "in", "on", "at", "to", "for",
  "with", "by", "from", "is", "are", "was", "were", "be", "been", "being",
  "as", "if", "then", "this", "that", "these", "those", "it", "its", "i",
  "you", "your", "we", "our", "he", "she", "they", "them", "have", "has",
  "had", "do", "does", "did", "will", "would", "should", "could", "can",
  "what", "which", "who", "whom", "when", "where", "why", "how", "about",
  "into", "out", "up", "down", "over", "under", "more", "most", "some",
  "any", "all", "no", "not", "only", "own", "same", "so", "than", "too",
  "very", "just", "also", "much", "many",
]);

export function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\-./@]/g, " ") // keep hyphens, slashes, dots, @ (for handles)
    .split(/\s+/)
    .filter((t) => t.length > 0)
    .filter((t) => !STOPWORDS.has(t))
    .map((t) => t.replace(/\.+$/, "")) // trailing dots
    .filter((t) => t.length > 0);
}

export type BM25Index = {
  k1: number;
  b: number;
  docs: Doc[];
  // per-doc: tokenised text + length
  tokenized: string[][];
  docLens: number[];
  avgLen: number;
  // term-doc frequencies: term -> docIndex -> count
  tf: Map<string, Map<number, number>>;
  // term -> # of docs containing it
  df: Map<string, number>;
  // total docs
  N: number;
};

export function buildIndex(docs: Doc[], k1 = 1.5, b = 0.75): BM25Index {
  const tokenized = docs.map((d) => tokenize(d.text));
  const docLens = tokenized.map((toks) => toks.length);
  const totalLen = docLens.reduce((a, n) => a + n, 0);
  const avgLen = totalLen / Math.max(1, docs.length);

  const tf: Map<string, Map<number, number>> = new Map();
  const df: Map<string, number> = new Map();

  for (let docIdx = 0; docIdx < tokenized.length; docIdx++) {
    const seen = new Set<string>();
    for (const term of tokenized[docIdx]) {
      // term-doc frequency
      let inner = tf.get(term);
      if (!inner) {
        inner = new Map();
        tf.set(term, inner);
      }
      inner.set(docIdx, (inner.get(docIdx) ?? 0) + 1);
      // doc frequency (per-doc, not per-occurrence)
      if (!seen.has(term)) {
        df.set(term, (df.get(term) ?? 0) + 1);
        seen.add(term);
      }
    }
  }

  return { k1, b, docs, tokenized, docLens, avgLen, tf, df, N: docs.length };
}

// IDF using BM25's "probabilistic" formula, clamped at 0 so common terms
// can't actively hurt the score.
function idf(idx: BM25Index, term: string): number {
  const dfTerm = idx.df.get(term) ?? 0;
  if (dfTerm === 0) return 0;
  const raw = Math.log((idx.N - dfTerm + 0.5) / (dfTerm + 0.5) + 1);
  return Math.max(0, raw);
}

export type BM25Hit = { docIdx: number; id: string; score: number };

export function search(
  idx: BM25Index,
  query: string,
  topK = 10,
): BM25Hit[] {
  const qTokens = tokenize(query);
  if (qTokens.length === 0) return [];

  // Score each candidate doc by accumulating BM25 contributions per term.
  const scores: Map<number, number> = new Map();

  for (const term of qTokens) {
    const idfTerm = idf(idx, term);
    if (idfTerm === 0) continue;
    const postings = idx.tf.get(term);
    if (!postings) continue;
    for (const [docIdx, freq] of postings) {
      const dl = idx.docLens[docIdx];
      const denom = freq + idx.k1 * (1 - idx.b + idx.b * (dl / idx.avgLen));
      const score = idfTerm * ((freq * (idx.k1 + 1)) / denom);
      scores.set(docIdx, (scores.get(docIdx) ?? 0) + score);
    }
  }

  const hits: BM25Hit[] = [];
  for (const [docIdx, score] of scores) {
    hits.push({ docIdx, id: idx.docs[docIdx].id, score });
  }
  hits.sort((a, b) => b.score - a.score);
  return hits.slice(0, topK);
}
